/*
 * ROKR bundle builder: lets customers add more models of the same series to the current product.
 * The markup is fully rendered by snippets/rokr-bundle-builder.liquid; this script only handles selection,
 * totals, the pegboard or scene preview and the add to cart request (integrated with the Prestige cart drawer).
 */

const WALL_LIMIT = 8;
const SVG_NS = 'http://www.w3.org/2000/svg';

/*
 * Scene items: percentages of a 4:3 picture drawn in a 1000 x 750 SVG. The model is centred on x and stands on y
 * (3 units lower so the base shadow meets the pad). Must match the base product maths in the snippet.
 */
const sceneItem = (data, key) => {
  const x = Number.parseFloat(data.sceneX);
  const y = Number.parseFloat(data.sceneY);
  const height = Number.parseFloat(data.sceneHeight) * 7.5;
  if (!data.src || ![x, y, height].every(Number.isFinite)) return null;

  return { key, src: data.src, title: data.title || '', x: x * 10 - height / 2, y: y * 7.5 - height + 3, height, base: y };
};

const toNumber = (value, fallback = 0) => {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : fallback;
};

const formatAmount = (cents, decimals, thousands = ',', decimal = '.') => {
  const [integer, fraction] = (cents / 100).toFixed(decimals).split('.');
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
};

const formatMoney = (cents, format) => {
  const value = Math.round(Number(cents) || 0);

  return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return formatAmount(value, 0);
      case 'amount_with_comma_separator':
        return formatAmount(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return formatAmount(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return formatAmount(value, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator':
        return formatAmount(value, 0, ' ', ',');
      case 'amount_with_space_separator':
        return formatAmount(value, 2, ' ', ',');
      case 'amount_with_period_and_space_separator':
        return formatAmount(value, 2, ' ', '.');
      default:
        return formatAmount(value, 2);
    }
  });
};

/*
 * Tiers from the series metaobject: "2:8, 3:12, 4:15" (count:rate, count includes the current product).
 * Invalid entries, zero rates and non-increasing rates are ignored; the last entry wins for a duplicated count.
 */
const parseTiers = (input) => {
  const tiers = String(input || '').split(/[,，;；\n]/).map((entry) => {
    const [count, rate] = entry.replace(/[%件]/g, '').split(/[:：]/).map((part) => Number(part.trim()));
    return { count, rate };
  });

  const byCount = new Map();
  tiers.forEach((tier) => {
    const count = Number(tier?.count);
    const rate = Number(tier?.rate);
    if (Number.isInteger(count) && count >= 2 && Number.isFinite(rate) && rate >= 0 && rate <= 100) {
      byCount.set(count, { count, rate });
    }
  });

  let previousRate = 0;
  return [...byCount.values()].sort((a, b) => a.count - b.count).filter((tier) => {
    if (tier.rate <= previousRate) return false;
    previousRate = tier.rate;
    return true;
  });
};

class RokrBundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.selected = new Set();
    this.busy = false;

    // Deduplicate options pointing to the same variant (the same product may be listed twice)
    const seen = new Set();
    this.options = [...this.querySelectorAll('[data-option]')].filter((option) => {
      if (seen.has(option.dataset.variantId)) {
        option.hidden = true;
        return false;
      }
      seen.add(option.dataset.variantId);
      return true;
    });
    this.optionsById = new Map(this.options.map((option) => [option.dataset.variantId, option]));
    this.accessory = this.options.find((option) => option.hasAttribute('data-accessory')) || null;

    this.addButton = this.querySelector('[data-add]');
    this.message = this.querySelector('[data-message]');
    this.wall = this.querySelector('[data-wall]');
    this.wallGrid = this.querySelector('[data-wall-grid]');
    this.baseArt = this.querySelector('template[data-base-art]')?.dataset || {};
    this.sceneLayers = this.querySelector('[data-scene-layers]');
    this.baseScene = this.querySelector('template[data-base-scene]')?.dataset || {};
    this.discountEnabled = this.dataset.discount === 'true';
    this.tiers = this.discountEnabled ? parseTiers(this.dataset.tiers) : [];

    if (this.options.length === 0 && !window.Shopify?.designMode) {
      this.hidden = true;
      return;
    }

    this.buildMilestones();
    this.syncIncluded();

    this.addEventListener('click', this.onClick.bind(this), { signal });
    document.addEventListener('variant:change', this.onVariantChange.bind(this), { signal });

    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  /* State ------------------------------------------------------------------------------------------------------------ */

  get baseTokens() {
    return (this.dataset.baseSku || '').toUpperCase().split('+').map((sku) => sku.trim()).filter(Boolean);
  }

  get baseAvailable() {
    return this.dataset.baseAvailable === 'true';
  }

  get selectedOptions() {
    return [...this.selected].map((id) => this.optionsById.get(id)).filter(Boolean);
  }

  /* Selected models, without the accessory (the accessory never counts as a model) */
  get selectedModels() {
    return this.selectedOptions.filter((option) => option !== this.accessory);
  }

  /* Models already contained in the current variant (for instance "LQB02+LQ901") */
  get includedOptions() {
    return this.options.filter((option) => option.dataset.included === 'true');
  }

  get count() {
    return Math.max(1, this.baseTokens.length) + this.selectedModels.length;
  }

  quantityOf(option) {
    return option === this.accessory ? Math.max(1, Math.round(toNumber(option.dataset.quantity, 1))) : 1;
  }

  totals() {
    const regular = this.selectedOptions.reduce(
      (sum, option) => sum + toNumber(option.dataset.price) * this.quantityOf(option),
      toNumber(this.dataset.basePrice)
    );
    const rate = this.tiers.reduce((current, tier) => (this.count >= tier.count ? tier.rate : current), 0);
    const savings = Math.round((regular * rate) / 100);

    return { regular, rate, savings, total: regular - savings };
  }

  money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat);
  }

  /* Events ----------------------------------------------------------------------------------------------------------- */

  onClick(event) {
    const step = event.target.closest('[data-quantity-step]');

    if (step && this.accessory?.contains(step)) {
      if (this.busy || this.accessory.dataset.available !== 'true') return;

      const max = toNumber(this.accessory.dataset.quantityMax, 8);
      const next = this.quantityOf(this.accessory) + toNumber(step.dataset.quantityStep);
      this.accessory.dataset.quantity = String(Math.min(max, Math.max(1, next)));
      this.selected.add(this.accessory.dataset.variantId);
      this.clearMessage();
      this.render();
      return;
    }

    const toggle = event.target.closest('.rokr-bundle__toggle');

    if (toggle && this.contains(toggle)) {
      const option = toggle.closest('[data-option]');
      if (this.busy || toggle.disabled || !option) return;

      const id = option.dataset.variantId;
      this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id);
      if (option !== this.accessory) this.syncAccessory();
      this.clearMessage();
      this.render();
      return;
    }

    if (event.target.closest('[data-add]')) {
      this.addToCart();
    }
  }

  matchesForm(formId) {
    if (!formId) return false;
    if (this.dataset.formId) return formId === this.dataset.formId;
    return formId.startsWith(`product-form-main-${this.dataset.productId}-`);
  }

  onVariantChange(event) {
    const { variant } = event.detail || {};
    // The theme sets detail.formId from form.id, which returns the "id" input of a product form, so read the
    // attribute of the form that dispatched the event instead.
    const formId = typeof event.detail?.formId === 'string' ? event.detail.formId : event.target?.getAttribute?.('id');
    if (!this.matchesForm(formId)) return;

    this.dataset.baseVariantId = variant?.id || '';
    this.dataset.basePrice = String(variant?.price || 0);
    this.dataset.baseSku = variant?.sku || '';
    this.dataset.baseAvailable = String(Boolean(variant?.available));

    this.syncIncluded();
    this.clearMessage();
    this.render();
  }

  /* One accessory (e.g. a connector) per added model; picking models resets any manual quantity, like SHOPLINE */
  syncAccessory() {
    if (!this.accessory) return;

    const models = this.selectedModels.length;
    const max = toNumber(this.accessory.dataset.quantityMax, 8);
    this.accessory.dataset.quantity = String(Math.min(max, Math.max(1, models)));

    if (models > 0 && this.accessory.dataset.available === 'true') {
      this.selected.add(this.accessory.dataset.variantId);
    } else {
      this.selected.delete(this.accessory.dataset.variantId);
    }
  }

  syncIncluded() {
    const tokens = this.baseTokens.slice(1);

    this.options.forEach((option) => {
      const included = tokens.includes(option.dataset.sku);
      option.dataset.included = String(included);
      option.querySelector('[data-included-flag]')?.toggleAttribute('hidden', !included);
      if (included) this.selected.delete(option.dataset.variantId);
    });
  }

  /* Rendering -------------------------------------------------------------------------------------------------------- */

  render() {
    const totals = this.totals();
    const count = this.count;

    this.options.forEach((option) => {
      const toggle = option.querySelector('.rokr-bundle__toggle');
      const unavailable = option.dataset.available !== 'true' || option.dataset.included === 'true';
      toggle.disabled = unavailable;
      toggle.setAttribute('aria-pressed', String(this.selected.has(option.dataset.variantId)));
    });

    if (this.accessory) {
      const quantity = String(this.quantityOf(this.accessory));
      this.accessory.querySelectorAll('[data-quantity-value]').forEach((node) => {
        if (node.textContent !== quantity) node.textContent = quantity;
      });
      const max = toNumber(this.accessory.dataset.quantityMax, 8);
      const locked = this.busy || this.accessory.dataset.available !== 'true';
      this.accessory.querySelectorAll('[data-quantity-step]').forEach((button) => {
        const down = toNumber(button.dataset.quantityStep) < 0;
        button.disabled = locked || (down ? Number(quantity) <= 1 : Number(quantity) >= max);
      });
    }

    this.setText('[data-count]', String(count));
    this.setText('[data-count-label]', count === 1 ? this.dataset.oneLabel : this.dataset.manyLabel);
    this.setText('[data-total]', this.money(totals.total));

    if (this.discountEnabled) {
      this.setText('[data-regular]', this.money(totals.regular));
      this.setText('[data-saving]', `−${this.money(totals.savings)}`);
      this.setText('[data-rate]', `${totals.rate}%`);
      this.setText('[data-summary-rate]', `${totals.rate}%`);
      this.setText('[data-unlock]', this.unlockMessage());
      this.renderProgress();
    }

    const canAdd = this.baseAvailable && this.selected.size > 0 && !this.busy;
    this.addButton.disabled = !canAdd;

    let label;
    if (this.busy) {
      label = this.dataset.addingLabel;
    } else if (!this.baseAvailable) {
      label = this.dataset.soldOutLabel;
    } else if (this.selected.size === 0) {
      label = this.dataset.emptyLabel;
    } else {
      label = this.dataset.buttonLabel.replace('{count}', String(count)).replace('{total}', this.money(totals.total));
    }
    this.setText('[data-add-label]', label);

    this.renderWall();
    this.renderScene();
  }

  setText(selector, text) {
    const element = this.querySelector(selector);
    if (element && element.textContent !== text) element.textContent = text;
  }

  unlockMessage() {
    if (this.tiers.length === 0) return '';
    if (!this.baseAvailable) return this.dataset.soldOutLabel;

    const maxCount = Math.max(1, this.baseTokens.length) + this.options.filter((option) => {
      return option.dataset.available === 'true' && option.dataset.included !== 'true';
    }).length;
    const next = this.tiers.find((tier) => tier.count > this.count && tier.count <= maxCount);

    if (!next) {
      return this.totals().rate > 0 ? this.dataset.highestLabel : '';
    }

    const remaining = next.count - this.count;
    return this.dataset.unlockLabel.replace('{remaining}', String(remaining)).replace('{rate}', String(next.rate));
  }

  buildMilestones() {
    const container = this.querySelector('[data-milestones]');
    if (!container) return;

    const reachable = 1 + this.options.length;
    this.progressTiers = this.tiers.filter((tier) => tier.count <= reachable);
    const discount = this.querySelector('[data-discount-ui]');

    if (this.progressTiers.length === 0) {
      discount?.setAttribute('hidden', '');
      return;
    }

    const points = [{ count: 1, rate: 0 }, ...this.progressTiers];
    container.replaceChildren(...points.map((tier, index) => {
      const node = document.createElement('span');
      node.className = 'rokr-bundle__milestone';
      node.dataset.count = String(tier.count);
      node.style.left = `${(index / (points.length - 1)) * 100}%`;
      node.textContent = tier.rate ? `${tier.count} · ${tier.rate}%` : '1';
      return node;
    }));
  }

  renderProgress() {
    if (!this.progressTiers?.length) return;

    const counts = [1, ...this.progressTiers.map((tier) => tier.count)];
    const count = this.count;
    let progress = 100;

    if (count < counts[counts.length - 1]) {
      const next = counts.findIndex((threshold) => threshold > count);
      const previous = counts[next - 1];
      progress = ((next - 1 + (count - previous) / (counts[next] - previous)) / (counts.length - 1)) * 100;
    }

    this.querySelector('[data-progress]').style.width = `${progress}%`;
    this.querySelectorAll('.rokr-bundle__milestone').forEach((node) => {
      node.classList.toggle('is-active', count >= Number(node.dataset.count));
    });
  }

  wallItems() {
    const fromOption = (option) => ({
      key: option.dataset.variantId,
      src: option.dataset.art,
      scale: option.dataset.scale,
      rotation: option.dataset.rotation,
      offsetX: option.dataset.offsetX,
      offsetY: option.dataset.offsetY,
    });

    return [
      {
        key: 'base',
        src: this.baseArt.src,
        scale: this.baseArt.scale,
        rotation: this.baseArt.rotation,
        offsetX: this.baseArt.offsetX,
        offsetY: this.baseArt.offsetY,
      },
      ...this.includedOptions.map(fromOption),
      ...this.selectedOptions.map(fromOption),
    ];
  }

  /* Keyed update: existing models keep their node (no flicker), only new ones animate in */
  renderWall() {
    if (!this.wall || !this.wallGrid) return;

    const items = this.wallItems();
    // A partial picture is misleading: hide the preview when a model has no artwork or there are too many
    const complete = items.length <= WALL_LIMIT && items.every((item) => item.src);
    this.wall.hidden = !complete;
    if (!complete) return;

    const existing = new Map([...this.wallGrid.children].map((node) => [node.dataset.key, node]));
    const width = items.length === WALL_LIMIT ? 14 : 19;

    const nodes = items.map((item, index) => {
      let node = existing.get(item.key);

      if (node) {
        existing.delete(item.key);
      } else {
        node = document.createElement('div');
        node.className = 'rokr-bundle__wall-item is-entering';
        node.dataset.key = item.key;
        node.addEventListener('animationend', () => node.classList.remove('is-entering'), { once: true });

        const image = new Image(600, 300);
        image.alt = '';
        image.decoding = 'async';
        node.append(image);
      }

      const image = node.querySelector('img');
      if (image.getAttribute('src') !== item.src) image.src = item.src;

      // Selections fill the next slot from left to right
      const left = items.length === WALL_LIMIT ? (index * (100 - width)) / (WALL_LIMIT - 1) : 1 + index * 13.15;
      const scale = toNumber(item.scale, 100);
      node.style.setProperty('--left', `${left}%`);
      node.style.setProperty('--width', `${width}%`);
      node.style.setProperty('--offset-x', `${toNumber(item.offsetX)}%`);
      node.style.setProperty('--offset-y', `${toNumber(item.offsetY)}px`);
      node.style.setProperty('--scale', String(scale > 0 ? scale / 100 : 1));
      node.style.setProperty('--rotation', `${toNumber(item.rotation)}deg`);

      return node;
    });

    existing.forEach((node) => node.remove());
    nodes.forEach((node, index) => {
      if (this.wallGrid.children[index] !== node) this.wallGrid.insertBefore(node, this.wallGrid.children[index] || null);
    });
    this.wallGrid.setAttribute('aria-label', this.dataset.wallCountLabel.replace('{count}', String(items.length)));
  }

  /* Scene: fixed spots on one picture; models further back (smaller y) are drawn first */
  renderScene() {
    if (!this.sceneLayers) return;

    const items = [
      sceneItem(this.baseScene, 'base'),
      ...[...this.includedOptions, ...this.selectedModels].map((option) => {
        return sceneItem({ ...option.dataset, src: option.dataset.art }, option.dataset.variantId);
      }),
    ].filter(Boolean).sort((a, b) => a.base - b.base || a.x - b.x);

    const existing = new Map([...this.sceneLayers.children].map((node) => [node.dataset.key, node]));

    const nodes = items.map((item) => {
      let node = existing.get(item.key);

      if (node) {
        existing.delete(item.key);
      } else {
        node = document.createElementNS(SVG_NS, 'image');
        node.dataset.key = item.key;
        node.setAttribute('preserveAspectRatio', 'xMidYMax meet');
        node.classList.add('is-entering');
        node.addEventListener('animationend', () => node.classList.remove('is-entering'), { once: true });
      }

      if (node.getAttribute('href') !== item.src) node.setAttribute('href', item.src);
      node.setAttribute('x', String(item.x));
      node.setAttribute('y', String(item.y));
      node.setAttribute('width', String(item.height));
      node.setAttribute('height', String(item.height));

      return node;
    });

    existing.forEach((node) => node.remove());
    nodes.forEach((node, index) => {
      if (this.sceneLayers.children[index] !== node) this.sceneLayers.insertBefore(node, this.sceneLayers.children[index] || null);
    });

    const titles = [this.baseScene.title, ...this.selectedModels.map((option) => option.dataset.title)].filter(Boolean);
    this.sceneLayers.setAttribute('aria-label', titles.join(', '));
  }

  /* Cart ------------------------------------------------------------------------------------------------------------- */

  setBusy(busy) {
    this.busy = busy;
    this.classList.toggle('is-busy', busy);
    this.addButton.setAttribute('aria-busy', String(busy));
    this.render();
  }

  clearMessage() {
    if (!this.message) return;
    this.message.textContent = '';
    this.message.classList.remove('is-error', 'is-success');
  }

  showMessage(text, type) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.classList.toggle('is-error', type === 'error');
    this.message.classList.toggle('is-success', type === 'success');
  }

  async addToCart() {
    if (this.busy || !this.baseAvailable || this.selected.size === 0) return;

    const root = window.Shopify?.routes?.root || '/';
    const properties = { _bundle_source: 'rokr-bundle-builder' };
    const items = [
      { id: Number(this.dataset.baseVariantId), quantity: 1, properties },
      ...this.selectedOptions.map((option) => ({
        id: Number(option.dataset.variantId),
        quantity: this.quantityOf(option),
        properties,
      })),
    ];

    // Let the cart drawer ask for the sections it needs, exactly like the theme product form does
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    this.clearMessage();
    this.setBusy(true);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
      });
      const json = await response.json().catch(() => ({}));

      if (!response.ok || json.status) {
        throw new Error(json.description || json.message || this.dataset.errorLabel);
      }

      const settings = window.themeVariables?.settings || {};
      if (settings.cartType === 'page' || settings.pageType === 'cart') {
        window.location.href = `${root}cart`;
        return;
      }

      const cart = await (await fetch(`${root}cart.js`)).json();
      cart.sections = json.sections;

      this.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: json.items || [json], cart },
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart },
      }));

      this.showMessage(this.dataset.successLabel, 'success');
    } catch (error) {
      console.error('Bundle add to cart error', error);
      this.showMessage(error.message || this.dataset.errorLabel, 'error');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      this.setBusy(false);
    }
  }
}

if (!window.customElements.get('rokr-bundle-builder')) {
  window.customElements.define('rokr-bundle-builder', RokrBundleBuilder);
}
